import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const FeatureAdoptionChart = ({ data }) => {
  if (!data || data?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
        <div className="text-center text-muted-foreground text-sm py-8">
          No feature adoption data available
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-therapeutic-lg p-3">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs text-muted-foreground">
              <span style={{ color: entry?.color }}>{entry?.name}:</span> {entry?.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Zap" size={20} className="text-primary" strokeWidth={2} />
        <h2 className="text-lg font-heading font-semibold text-foreground">
          Feature Adoption Rates
        </h2>
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis 
            dataKey="feature" 
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: '12px' }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: '12px' }}
            label={{ value: 'Adoption Rate (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ fontSize: '12px' }}
            iconType="square"
          />
          <Bar 
            dataKey="adoptionRate" 
            name="Overall Adoption"
            fill="hsl(var(--primary))" 
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="premiumAdoption" 
            name="Premium User Adoption"
            fill="hsl(var(--success))" 
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Most Adopted Feature</p>
          <p className="text-sm font-semibold text-foreground">
            {data?.reduce((max, item) => item?.adoptionRate > max?.adoptionRate ? item : max, data?.[0])?.feature}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Highest Premium Correlation</p>
          <p className="text-sm font-semibold text-success">
            {data?.reduce((max, item) => item?.premiumAdoption > max?.premiumAdoption ? item : max, data?.[0])?.feature}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Average Adoption Rate</p>
          <p className="text-sm font-semibold text-foreground">
            {(data?.reduce((sum, item) => sum + item?.adoptionRate, 0) / data?.length)?.toFixed(1)}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeatureAdoptionChart;